import { useEffect, useRef, useState } from 'react';
import Spinner from 'react-spinkit';
import { getCurrentYearTeachersData, getTotalRow } from '../reportUtilities';
import SortableTable from '../Tables/SortableTable';
import { cloneDeep } from 'lodash';
import styles from './TableStyles.module.scss';
import {
  ReportStudent,
  ReportingMixpanelEvent,
  TeacherClassroomMaps,
} from '../../../peekapak-types/ReportDataExchangeTypes';
import { getLegacyStudentLogins, getNewStudentLogins } from '../reportUtilities';
import { UserProfileType } from '../../../peekapak-types/DataProtocolTypes';

interface OverallUsageProps {
  errorMessage: string;
  journalsMap: Record<string, unknown>;
  journalsMapReady: boolean;
  journalsMapLoadProgress?: string;
  legacyPeekavilleEvents: ReportingMixpanelEvent[];
  legacyPeekavilleEventsReady: boolean;
  teacherStudentMap: Record<string, ReportStudent[]>;
  teacherStudentMapReady: boolean;
  teacherClassroomMaps: TeacherClassroomMaps;
  teacherClassroomMapsReady: boolean;
  userList: UserProfileType[];
  legacyPlatformEvents: ReportingMixpanelEvent[];
  legacyPlatformEventsReady: boolean;
  newMixpanelEvents: ReportingMixpanelEvent[];
  newMixpanelEventsReady: boolean;
}

const StudentsSummary = ({
  teacherStudentMapReady,
  teacherStudentMap,
  teacherClassroomMaps,
  teacherClassroomMapsReady,
  userList,
  legacyPeekavilleEvents,
  legacyPeekavilleEventsReady,
  journalsMap,
  journalsMapReady,
  journalsMapLoadProgress,
  errorMessage,
  legacyPlatformEvents,
  legacyPlatformEventsReady,
  newMixpanelEvents,
  newMixpanelEventsReady,
}: OverallUsageProps) => {
  const [myState, setMyState] = useState({
    dataMap: {},
    dataMapReady: false,
  });

  // const prevMixpanelEventsReady = useRef(
  //   legacyPeekavilleEventsReady &&
  //     legacyPlatformEventsReady &&
  //     newMixpanelEventsReady
  // );

  // useEffect(() => {
  //   if (
  //     prevMixpanelEventsReady.current &&
  //     (!legacyPeekavilleEventsReady ||
  //       !legacyPlatformEventsReady ||
  //       !newMixpanelEventsReady)
  //   ) {
  //     setMyState((prevState) => ({
  //       ...prevState,
  //       dataMap: {},
  //       dataMapReady: false,
  //     }));
  //   }
  //   prevMixpanelEventsReady.current =
  //     legacyPeekavilleEventsReady &&
  //     legacyPlatformEventsReady &&
  //     newMixpanelEventsReady;
  // }, [
  //   legacyPeekavilleEventsReady,
  //   legacyPlatformEventsReady,
  //   newMixpanelEventsReady,
  // ]);
  useEffect(() => {
    setMyState((prevState) => ({
      ...prevState,
      dataMap: {},
      dataMapReady: false,
    }));
  }, [legacyPlatformEvents, legacyPeekavilleEvents, newMixpanelEvents, journalsMap]);

  useEffect(() => {
    if (
      journalsMapReady &&
      legacyPeekavilleEventsReady &&
      teacherStudentMapReady &&
      teacherClassroomMapsReady &&
      legacyPlatformEventsReady &&
      newMixpanelEventsReady &&
      !myState.dataMapReady
    ) {
      buildData();
    }

    function buildData() {
      const newMap = {};
      const combinedEvents = [...legacyPeekavilleEvents, ...legacyPlatformEvents, ...newMixpanelEvents];

      Object.keys(teacherStudentMap).forEach((teacherKey) => {
        const newStudentsArray = teacherStudentMap[teacherKey].map((s) => {
          const events = combinedEvents.filter((event) => (event.userId || event.distinct_id) === s.userId);

          return {
            ...s,
            events,
            journals: journalsMap[s.userId],
          };
        });

        newMap[teacherKey] = newStudentsArray;
      });

      setMyState((prevState) => ({
        ...prevState,
        dataMap: newMap,
        dataMapReady: true,
      }));
    }
  }, [
    teacherClassroomMapsReady,
    legacyPeekavilleEventsReady,
    legacyPeekavilleEvents,
    teacherStudentMap,
    teacherStudentMapReady,
    journalsMap,
    journalsMapReady,
    myState.dataMapReady,
    legacyPlatformEventsReady,
    newMixpanelEventsReady,
    userList,
  ]);

  const message = getDataNotReadyMessage();
  if (message) {
    return message;
  }

  const staticMessage = getStaticMessage();
  if (staticMessage) {
    return message;
  }

  const rosteredTeachers = getCurrentYearTeachersData(
    userList,
    teacherClassroomMaps,
    legacyPlatformEvents,
    newMixpanelEvents,
  );

  const sortedList = cloneDeep(rosteredTeachers).sort((a, b) => (a.lastName || '').localeCompare(b.lastName || ''));

  const tableData = sortedList.map((e) => {
    return {
      teacher_name: `${e.lastName || ''}, ${e.firstName || ''}`,
      number_of_students: getNumberOfRosteredStudents(e.email),
      student_logins: getNumberOfStudentLogins(e.email),
      feelings_checkins: getNumberOfJournals(e.email),
      email: e.email,
      userId: e.userId,
    };
  });

  const columns = [
    {
      label: 'Teacher’s Name',
      accessor: 'teacher_name',
      sortable: false,
      renderTooltip: (data: { email?: string; userId?: string }) => data.email || data.userId || null,
    },
    {
      label: 'Number of Students',
      accessor: 'number_of_students',
      sortable: true,
      tooltip: 'Number of students currently rostered in a classroom associated with this teacher.',
    },
    {
      label: 'Student Logins',
      accessor: 'student_logins',
      sortable: true,
      tooltip: 'Logins by students currently rostered in a classroom associated with this teacher.',
    },
    {
      label: 'Feelings Check-Ins',
      accessor: 'feelings_checkins',
      sortable: true,
      tooltip:
        'Check-ins in the myPeekaville game or on the student platform by students currently rostered in a classroom associated with this teacher.',
    },
  ];

  const totalRow = getTotalRow(tableData);

  // console.debug(
  //   `%cStudent Analytics active students`,
  //   'background: blue; color: yellow',
  //   totalActiveStudents
  // );

  return (
    <div>
      <SortableTable data={tableData} columns={columns} totals={totalRow} />
    </div>
  );

  function getStaticMessage() {
    if (userList.length === 0) {
      return (
        <div className={styles.messageContainer}>
          <div className={styles.progressMessage}>There are no users in this school</div>
        </div>
      );
    }

    if (errorMessage) {
      return (
        <div className={styles.messageContainer}>
          <div className={styles.progressMessage}>
            {`We're sorry, there's a problem retrieving data. Please contact
            Peekapak support and provide them with this error message:`}
            {errorMessage}
          </div>
        </div>
      );
    }
  }

  function getDataNotReadyMessage() {
    let message = null;

    if (!teacherStudentMapReady) {
      message = 'Matching students to teachers...';
    } else if (!legacyPeekavilleEventsReady) {
      message = 'Loading game events data...';
    } else if (!journalsMapReady) {
      message = `Loading student check-in data ${journalsMapLoadProgress}...`;
    } else if (!myState.dataMapReady) {
      message = 'Calculating stats...';
    }

    if (message) {
      return (
        <div className={styles.messageContainer}>
          <div className={styles.progressMessage}>
            {message}
            <Spinner name='line-scale' />
            This may take up to 10 minutes or more
          </div>
        </div>
      );
    }

    return null;
  }

  function getNumberOfJournals(email: string) {
    const studentArray = myState.dataMap[email];
    if (studentArray && studentArray.length > 0) {
      const numberOfJournalEntries = studentArray.map((s) => {
        if (s.journals) {
          return s.journals.reduce((acc, j) => acc + 1, 0);
        }

        return 0;
      });

      return numberOfJournalEntries.reduce((acc, e) => acc + e, 0);
    }
    return 0;
  }

  function getNumberOfStudentLogins(email: string) {
    const studentArray = myState.dataMap[email];
    const eventsArray = studentArray.reduce((acc, s) => [...acc, ...s.events], []);

    return getLegacyStudentLogins(eventsArray) + getNewStudentLogins(eventsArray);
  }

  function getNumberOfRosteredStudents(email: string) {
    const studentArray = myState.dataMap[email];
    if (studentArray && studentArray.length > 0) {
      return studentArray.length;
    }
    return 0;
  }
};

export default StudentsSummary;
